import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import {
    Autocomplete,
    MessagePropType,
    getLanguageFromLocale,
} from '@ratehub/base-ui';

import fetchCreditCardList from '../functions/fetchCreditCardList';


function CreditCardPicker({
    id,
    name,

    value,
    province,

    onChange,
    onValidityChange,
    onValidate,
    onBlur,

    isRequired,
    isDisabled,

    placeholder,
    label,

    canShowInvalid,

    onCreateField,

    clearInputOnSelect = false,

    ...otherProps
}) {
    const intl = useIntl();
    const languageCode = getLanguageFromLocale(intl.locale);

    /**
     * Returns matching card names where each part of the target search matches the start of a word in the card name.
     * @param {string} query - what the user has typed trying to match a credit card name
     * @returns {({name: string, id: string, provider: object; slug: string})[]|[]} - a simplified credit card list
     */
    async function fetchCreditCards(query) {
        if (!query) {
            return [];
        }

        const creditCards = await fetchCreditCardList({
            province,
            language: languageCode,
        });

        // create a RegExp where each word in query must match the start of a word in the card name
        // \s is any white space. As of this writing, some credit cards have non-breaking spaces in their names.
        const escapedRegExp = '(^|.+\\s)' + query.split(' ')
            .filter(word => word)
            .map(escapeRegExp)
            .join('.*\\s');

        const regex = new RegExp(escapedRegExp, 'i');
        return creditCards.filter(card => card.name.match(regex));
    }


    return (
        <Autocomplete
            id={id}
            name={name}
            value={value}
            onChange={onChange}
            onValidityChange={onValidityChange}
            onValidate={onValidate}
            onBlur={onBlur}
            isRequired={isRequired}
            isDisabled={isDisabled}
            canShowInvalid={canShowInvalid}
            onCreateField={onCreateField}
            fetchSuggestions={fetchCreditCards}
            suggestionRenderer={suggestion => suggestion.name}
            label={label}
            placeholder={placeholder}
            clearInputOnSelect={clearInputOnSelect}
            {...otherProps}
        />
    );
}

CreditCardPicker.propTypes = {
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    value: PropTypes.any,
    province: PropTypes.string,

    onChange: PropTypes.func,
    onValidityChange: PropTypes.func,
    onValidate: PropTypes.func,
    onBlur: PropTypes.func,

    isDisabled: PropTypes.bool,
    isRequired: PropTypes.bool,

    label: MessagePropType,
    placeholder: MessagePropType,

    canShowInvalid: PropTypes.bool,

    onCreateField: PropTypes.func,

    clearInputOnSelect: PropTypes.bool,
};


/**
 * Returns a string with special characters escaped for creating a RegExp.
 * @param {string} string - a target string that will be used to create a RegExp, but with special RegExp
 *                          characters within it escaped
 * @returns {string} - the incoming string with special characters escaped
 */
function escapeRegExp(string) {
    // \$& means the whole matched string (a single character in this case)
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}


export default CreditCardPicker;
